import { UserSettingsContextType } from './user-settings-context.type';
import { createContext, ReactNode, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { useUserSettings } from 'src/hooks/use-user-settings';
import i18n from 'src/i18n';

// Create the user settings context with an initial undefined value.
const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

/**
 * UserSettingsProvider component wraps the application and provides user settings context.
 * @param children - The components that will have access to the user settings context.
 */
export function UserSettingsProvider({ children }: { children?: ReactNode }) {
  const userSettings = useUserSettings(); // Retrieve user settings state and methods from the custom hook.

  // Provide the user settings context to the component tree.
  return <UserSettingsContext.Provider value={userSettings}>{children || <Outlet />}</UserSettingsContext.Provider>;
}

/**
 * Custom hook to access the user settings context.
 * Throws an error if used outside of an UserSettingsProvider.
 */
export function useUserSettingsContext() {
  const context = useContext(UserSettingsContext); // Access the user settings context.

  // Throw an error if the hook is used outside of an UserSettingsProvider.
  if (!context) {
    throw new Error(i18n.t('ERRORS.CONTEXT.USER_SETTINGS'));
  }

  return context; // Return the context value if it exists.
}
